/**
 * src/pages/jobwork/CourierPortal.js
 *
 * Shown after a successful login with role 'courier'. Same page family as
 * JobWorkPortal.js (both are reached via the shared /job-work entry point
 * in JobWorkPage.js, which branches on session.user.role) — courier used to
 * get a near-full copy of admin.marqlandstudios.com's CourierTracking.js
 * (see that file's header comment), which let a courier see/edit/delete
 * ANY shipment, not just their own. This portal is properly scoped: a
 * courier only ever sees shipments they created (backend enforces the same
 * ownership check on every verb — see routes/shipmentRoutes.js).
 *
 * Status-grouping logic (COMPLETED_STATUSES / ARCHIVE_THRESHOLD_DAYS) is
 * copied verbatim from CourierTracking.js so the three tabs behave
 * identically to what a courier is used to. The background auto-tracking
 * scheduler (services/shipmentTrackingService.js) polls ALL shipments
 * regardless of owner, so a courier's shipments still get automatic status
 * updates exactly like before — nothing here changes that.
 *
 * Talks to /api/shipments/* and /api/shipping-partners (read-only, for the
 * partner-name dropdown) — both already existing, unmodified endpoints.
 */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import NavBar from '../../components/NavBar';
import BarcodeScannerModal from '../../components/BarcodeScannerModal';
import {
  Plus, Trash2, Pencil, X, Search, Clock, CheckCircle2,
  Archive, Loader2, Truck, RotateCcw, Camera as CameraIcon, Boxes, ImagePlus, Image as ImageIcon,
  ScanBarcode, AlertTriangle,
} from 'lucide-react';
import AirDatePicker from '../../components/AirDatePicker';
import { MARQLAND_THEME_CSS } from '../../styles/marqlandTheme';
import { AIR_CSS, AirSelect } from '../../styles/airTheme';
import { isValidMessage, sanitizeMessage, GENERIC_INVALID_MESSAGE } from '../../utils/jobWorkValidation';
import { INDIA_STATES, CITIES_BY_STATE } from '../../utils/indiaLocations';
import { parseLabelText } from './labelParser';

const API_BASE = process.env.REACT_APP_API_URL || '';

// ── Same constants as admin's CourierTracking.js, kept in sync intentionally ──
const COMPLETED_STATUSES = ['Delivered', 'Completed', 'Returned'];
const ARCHIVE_THRESHOLD_DAYS = 30;
const STATUS_OPTIONS = ['Pending', 'Booked', 'In Transit', 'Out for Delivery', 'Delivered', 'Completed', 'Returned', 'Exception'];

const isArchived = (s) => {
  if (!COMPLETED_STATUSES.includes(s.status)) return false;
  const anchor = s.lastTrackedAt || s.updatedAt || s.shippedDate || s.createdAt;
  return (Date.now() - new Date(anchor).getTime()) / 86_400_000 > ARCHIVE_THRESHOLD_DAYS;
};

const TABS = [
  { id: 'active', label: 'Active', icon: Clock },
  { id: 'completed', label: 'Completed', icon: CheckCircle2 },
  { id: 'archive', label: 'Archive', icon: Archive },
];

const emptyForm = () => ({
  shippedDate: new Date().toISOString().slice(0, 10),
  recipientName: '', recipientAddress: '', country: 'India', city: '', state: '', phone: '',
  trackingId: '', shippingPartner: '', status: 'Pending', notes: '', orderId: null,
  boxes: '', photoUrl: '', extraPhotos: [],
});

const boxesLabel = (n) => (n || n === 0 ? `${n} box${n === 1 ? '' : 'es'}` : '');
const cleanBoxes = (v) => String(v ?? '').replace(/\D/g, '').slice(0, 4);
const safeImg = (u) => (typeof u === 'string' && /^https?:\/\//i.test(u.trim()) ? u.trim() : '');
// A shipment's photos: photoUrl is the main one, extraPhotos the rest (server keeps up to 13 in all).
const MAX_PHOTOS = 13;
const photosOf = (s) => [s?.photoUrl, ...(Array.isArray(s?.extraPhotos) ? s.extraPhotos : [])].filter(Boolean);
const photoFields = (list) => ({ photoUrl: list[0] || '', extraPhotos: list.slice(1, MAX_PHOTOS) });

// Live camera scanning needs the browser's BarcodeDetector (not in iPhone/iPad
// Safari); a photo of the AWB sticker is read on the server and works everywhere.
const LIVE_SCAN = typeof window !== 'undefined' && 'BarcodeDetector' in window;
// Rows still to complete — e.g. imported from Excel by Marqland without an AWB or photo.
const missingOf = (s) => ({ awb: !String(s.trackingId || '').trim(), photo: !s.photoUrl });
const MissingFlags = ({ s }) => (missingOf(s).awb || missingOf(s).photo ? (
  <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 6 }}>
    {missingOf(s).awb && <span style={MISSING_BADGE}><AlertTriangle size={11} /> AWB missing</span>}
    {missingOf(s).photo && <span style={MISSING_BADGE}><AlertTriangle size={11} /> Photo missing</span>}
  </span>
) : null);
const ROW_FIX_BTN = {
  display: 'inline-flex', alignItems: 'center', gap: 5, background: '#fff', border: '1px solid #b0b0b0', borderRadius: 8,
  padding: '6px 10px', fontSize: 12, color: '#222222', cursor: 'pointer', minHeight: 32,
};
const MISSING_BADGE = {
  display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap',
  color: '#8a5a00', background: '#fdf3e1', border: '1px solid #f3e2bf', borderRadius: 10, padding: '1px 7px',
};

// Phone photos are shrunk in the browser before upload (a format the browser
// can't draw, like some HEIC, goes as-is).
const shrinkPhoto = (file, maxDim, name) => new Promise((resolve) => {
  const url = URL.createObjectURL(file);
  const img = new Image();
  img.onload = () => {
    const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(img.width * scale);
    canvas.height = Math.round(img.height * scale);
    canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
    URL.revokeObjectURL(url);
    canvas.toBlob((blob) => resolve(blob ? new File([blob], name, { type: 'image/jpeg' }) : file), 'image/jpeg', 0.9);
  };
  img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
  img.src = url;
});

// Clicking a shipment row opens its details — except on the row's own controls.
const isControlClick = (e) => !!e.target.closest('button, a, input, select, label, [role="listbox"], [role="option"]');

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');
const distinctSorted = (arr, key) => [...new Set(arr.map(s => s[key]).filter(Boolean))].sort();

// Camera-based barcode scan is a mobile-only affordance — on desktop a
// courier just types/pastes the tracking ID as before, so the scan button
// only renders below this breakpoint.
const MOBILE_BREAKPOINT = 640;
const useIsMobile = (breakpoint = MOBILE_BREAKPOINT) => {
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.innerWidth <= breakpoint
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= breakpoint);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [breakpoint]);
  return isMobile;
};


// Details + photo of one shipment, opened by clicking its row.
const ShipmentDetails = ({ s, onClose, onEdit }) => {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);
  const photo = safeImg(s.photoUrl);
  const extraPhotos = (s.extraPhotos || []).map(safeImg).filter(Boolean);
  const rows = [
    ['Shipped', fmtDate(s.shippedDate)], ['Status', s.status], ['Boxes', boxesLabel(s.boxes)],
    ['Tracking ID', s.trackingId], ['Partner', s.shippingPartner], ['Phone', s.phone],
    ['Address', [s.recipientAddress, s.city, s.state, s.country].filter(Boolean).join(', ')], ['Notes', s.notes],
  ].filter(([, v]) => v);
  return (
    <div role="dialog" aria-modal="true" aria-label={`Shipment for ${s.recipientName}`} onClick={onClose}
      style={{ position: 'fixed', inset: 0, zIndex: 300, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 12, boxShadow: '0 6px 20px rgba(0,0,0,.2)', width: '100%', maxWidth: photo ? 860 : 520, maxHeight: '90vh', overflowY: 'auto', padding: 24, position: 'relative' }}>
        <button onClick={onClose} aria-label="Close" style={{ position: 'absolute', top: 14, right: 14, background: 'none', border: 'none', cursor: 'pointer', color: '#6a6a6a' }}><X size={18} /></button>
        <h2 className="sf" style={{ fontSize: 22, fontWeight: 500, margin: '0 0 16px', paddingRight: 30 }}>{s.recipientName}</h2>
        <div style={{ display: 'grid', gridTemplateColumns: photo && window.innerWidth > 640 ? 'minmax(0,1.2fr) minmax(0,1fr)' : '1fr', gap: 20 }}>
          {photo && (
            <a href={photo} target="_blank" rel="noopener noreferrer" title="Open full size">
              <img src={photo} alt="Shipment" style={{ width: '100%', maxHeight: '60vh', objectFit: 'contain', borderRadius: 12, background: '#f7f7f7', display: 'block' }} />
            </a>
          )}
          <dl style={{ margin: 0, display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '10px 14px', alignContent: 'start', fontSize: 14 }}>
            {rows.map(([k, v]) => (
              <React.Fragment key={k}>
                <dt style={{ color: '#6a6a6a', fontSize: 13 }}>{k}</dt>
                <dd style={{ margin: 0, color: '#222222', wordBreak: 'break-word' }}>{v}</dd>
              </React.Fragment>
            ))}
            {!photo && (<><dt style={{ color: '#6a6a6a', fontSize: 13 }}>Photo</dt><dd style={{ margin: 0, color: '#6a6a6a' }}>No photo yet</dd></>)}
            {extraPhotos.length > 0 && (
              <>
                <dt style={{ color: '#6a6a6a', fontSize: 13 }}>More photos</dt>
                <dd style={{ margin: 0, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {extraPhotos.map((url, i) => (
                    <a key={url} href={url} target="_blank" rel="noopener noreferrer" title="Open full size">
                      <img src={url} alt={`Shipment, view ${i + 2}`} style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 8, border: '1px solid #dddddd', display: 'block' }} />
                    </a>
                  ))}
                </dd>
              </>
            )}
          </dl>
        </div>
        {onEdit && (
          <button onClick={() => { onClose(); onEdit(s); }} className="btn-gold" style={{ marginTop: 20, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Pencil size={14} /> Edit shipment
          </button>
        )}
      </div>
    </div>
  );
};

const CourierPortal = ({ session, onLogout }) => {
  const authHeader = { Authorization: `Bearer ${session.accessToken}` };
  const isMobile = useIsMobile();
  // Below laptop width the 7-column table would need sideways scrolling —
  // shipments show as stacked cards instead.
  const isCompact = useIsMobile(1100);

  const [tab, setTab] = useState('active');
  const [shipments, setShipments] = useState([]);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const [search, setSearch] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [filterPartner, setFilterPartner] = useState('');
  const [filterCountry, setFilterCountry] = useState('');
  const [filterState, setFilterState] = useState('');
  const [filterCity, setFilterCity] = useState('');

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [cityManual, setCityManual] = useState(false);
  const [formErrors, setFormErrors] = useState([]);
  const [saving, setSaving] = useState(false);
  const [statusUpdatingId, setStatusUpdatingId] = useState(null);
  const [showScanner, setShowScanner] = useState(false);
  const [filterStatus, setFilterStatus] = useState('');
  const [viewing, setViewing] = useState(null);
  const [photoBusy, setPhotoBusy] = useState(false);
  const photoInput = useRef(null);
  const [onlyMissing, setOnlyMissing] = useState(false);
  // Close-up of the To label → OCR on the server, which also stores it as the main photo.
  const labelInput = useRef(null);
  const [scanningLabel, setScanningLabel] = useState(false);
  const [labelNote, setLabelNote] = useState('');
  // Photo of the AWB sticker → barcode read on the server. Target: 'form' or a shipment row.
  const awbInput = useRef(null);
  const awbFor = useRef(null);
  const [awbBusyId, setAwbBusyId] = useState(null);
  // A row's quick "add photo" (rows missing one).
  const rowPhotoInput = useRef(null);
  const rowPhotoFor = useRef(null);
  const [rowPhotoBusyId, setRowPhotoBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const res = await axios.get(`${API_BASE}/api/shipments`, { headers: authHeader });
      setShipments(res.data);
    } catch (err) {
      setLoadError(err.response?.data?.message || 'Failed to load your shipments.');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    axios.get(`${API_BASE}/api/shipping-partners`, { headers: authHeader })
      .then(res => setPartners(res.data))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openCreate = () => {
    setEditingId(null);
    setLabelNote('');
    setForm(emptyForm());
    setCityManual(false);
    setFormErrors([]);
    setShowForm(true);
  };

  const openEdit = (s) => {
    setEditingId(s._id);
    setLabelNote('');
    setForm({
      shippedDate: (s.shippedDate || '').slice(0, 10),
      recipientName: s.recipientName || '', recipientAddress: s.recipientAddress || '',
      country: s.country || 'India', city: s.city || '', state: s.state || '', phone: s.phone || '',
      trackingId: s.trackingId || '', shippingPartner: s.shippingPartner || '',
      status: s.status || 'Pending', notes: s.notes || '', orderId: s.orderId || null,
      boxes: s.boxes ?? '', photoUrl: s.photoUrl || '', extraPhotos: s.extraPhotos || [],
    });
    // If the shipment's saved city isn't in our known list for its state
    // (or its state isn't recognised at all), default to manual entry so
    // the existing value isn't silently lost.
    const knownCities = CITIES_BY_STATE[s.state] || [];
    setCityManual(!!s.city && !knownCities.includes(s.city));
    setFormErrors([]);
    setShowForm(true);
  };

  const handleStateChange = (newState) => {
    setForm(f => ({ ...f, state: newState, city: '' }));
    setCityManual(false);
  };

  // Scanned value overwrites whatever was typed so far — the courier tapped
  // "scan" specifically to avoid typing, so partial manual input shouldn't linger.
  const handleBarcodeDetected = (code) => {
    setForm(f => ({ ...f, trackingId: code }));
    setShowScanner(false);
  };

  // Close-up photo of the printed To label → recipient fields + main shipment photo.
  // Only fields the label actually yielded are overwritten.
  const scanLabel = async (file) => {
    if (!file) return;
    setScanningLabel(true);
    setLabelNote('');
    try {
      const fd = new FormData();
      fd.append('image', await shrinkPhoto(file, 2400, 'label.jpg'));
      const { data: d } = await axios.post(`${API_BASE}/api/shipments/scan-photo`, fd, { headers: authHeader, timeout: 120000 });
      const parsed = parseLabelText(d?.candidates?.length ? d.candidates : d?.text);
      const found = ['recipientName', 'recipientAddress', 'city', 'state', 'phone'].filter((k) => parsed[k]);
      const awb = String(d?.trackingId || '').trim();
      setForm((f) => ({
        ...f,
        ...Object.fromEntries(found.map((k) => [k, parsed[k]])),
        ...(awb && !f.trackingId ? { trackingId: awb } : {}),
        ...(d?.photoUrl ? photoFields([d.photoUrl, ...photosOf(f)]) : {}),
      }));
      if (parsed.city) setCityManual(!(CITIES_BY_STATE[parsed.state] || []).includes(parsed.city));
      setLabelNote(found.length ? 'Label read — check the details before saving.' : "Couldn't read the label — try a sharper, straight-on photo, or type the details.");
    } catch (err) {
      setLabelNote(err.response?.data?.message || "Couldn't read the label — type the details instead.");
    } finally {
      setScanningLabel(false);
    }
  };

  // Photo of the AWB barcode sticker → the AWB, into the form or straight onto a row.
  const pickAwbPhoto = (target) => { awbFor.current = target; awbInput.current?.click(); };
  const readAwbPhoto = async (file) => {
    const target = awbFor.current;
    if (!file || !target) return;
    setAwbBusyId(target === 'form' ? 'form' : target._id);
    try {
      const fd = new FormData();
      fd.append('image', await shrinkPhoto(file, 2400, 'awb.jpg'));
      const { data: d } = await axios.post(`${API_BASE}/api/shipments/scan-barcode`, fd, { headers: authHeader, timeout: 60000 });
      const awb = String(d?.trackingId || d?.barcodes?.[0]?.text || '').trim();
      if (!awb) {
        window.alert('No barcode found. Take a closer, sharper photo of the AWB sticker, or type the number.');
      } else if (target === 'form') {
        setForm((f) => ({ ...f, trackingId: awb }));
      } else {
        await axios.put(`${API_BASE}/api/shipments/${target._id}`, { trackingId: awb, orderId: target.orderId || null }, { headers: authHeader });
        await load();
      }
    } catch (err) {
      window.alert(err.response?.data?.message || "Couldn't read the barcode — type the AWB instead.");
    } finally {
      setAwbBusyId(null);
    }
  };

  // A row missing a photo: pick one or more, upload, save onto the row.
  const pickRowPhotos = (s) => { rowPhotoFor.current = s; rowPhotoInput.current?.click(); };
  const uploadRowPhotos = async (fileList) => {
    const s = rowPhotoFor.current;
    const files = Array.from(fileList || []).slice(0, MAX_PHOTOS - photosOf(s || {}).length);
    if (!s || !files.length) return;
    setRowPhotoBusyId(s._id);
    const urls = [];
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append('image', await shrinkPhoto(file, 1600, 'shipment.jpg'));
        const res = await axios.post(`${API_BASE}/api/shipments/photo`, fd, { headers: authHeader, timeout: 120000 });
        if (res.data?.url) urls.push(res.data.url);
      }
    } catch (err) {
      window.alert(err.response?.data?.message || 'Photo upload failed.');
    } finally {
      try {
        if (urls.length) {
          await axios.put(`${API_BASE}/api/shipments/${s._id}`, { ...photoFields([...photosOf(s), ...urls]), orderId: s.orderId || null }, { headers: authHeader });
          await load();
        }
      } catch (err) {
        window.alert(err.response?.data?.message || 'Could not save the photo.');
      }
      setRowPhotoBusyId(null);
    }
  };

  // Quick fixes on a row that is missing its AWB or photo.
  const rowFixes = (s) => (tab !== 'archive' && (missingOf(s).awb || missingOf(s).photo) ? (
    <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 6 }}>
      {missingOf(s).awb && (
        <button type="button" onClick={() => pickAwbPhoto(s)} disabled={awbBusyId === s._id} style={ROW_FIX_BTN}>
          {awbBusyId === s._id ? <Loader2 size={12} className="animate-spin" /> : <ScanBarcode size={12} />} {awbBusyId === s._id ? 'Reading…' : 'AWB from photo'}
        </button>
      )}
      {missingOf(s).photo && (
        <button type="button" onClick={() => pickRowPhotos(s)} disabled={rowPhotoBusyId === s._id} style={ROW_FIX_BTN}>
          {rowPhotoBusyId === s._id ? <Loader2 size={12} className="animate-spin" /> : <ImagePlus size={12} />} {rowPhotoBusyId === s._id ? 'Uploading…' : 'Add photo'}
        </button>
      )}
    </span>
  ) : null);

  const validateForm = () => {
    const errs = [];
    if (!form.recipientName.trim()) errs.push('Recipient name is required.');
    if (!form.shippedDate) errs.push('Shipped date is required.');
    if (form.notes && !isValidMessage(form.notes)) errs.push('Notes: ' + GENERIC_INVALID_MESSAGE);
    setFormErrors(errs);
    return errs.length === 0;
  };

  // Photos of the packed shipment — uploaded straight away, the form keeps their
  // URLs. Several can be picked at once; the ones uploaded stay if a later one fails.
  const uploadPhotos = async (fileList) => {
    const files = Array.from(fileList || []).slice(0, MAX_PHOTOS - photosOf(form).length);
    if (!files.length) return;
    setPhotoBusy(true);
    const urls = [];
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append('image', file);
        const res = await axios.post(`${API_BASE}/api/shipments/photo`, fd, { headers: authHeader, timeout: 120000 });
        if (res.data?.url) urls.push(res.data.url);
      }
    } catch (err) {
      setFormErrors([err.response?.data?.message || 'Photo upload failed.']);
    } finally {
      if (urls.length) setForm(f => ({ ...f, ...photoFields([...photosOf(f), ...urls]) }));
      setPhotoBusy(false);
    }
  };

  const submitForm = async () => {
    if (!validateForm()) return;
    setSaving(true);
    try {
      const body = { ...form, boxes: form.boxes === '' ? null : Number(form.boxes) };
      if (editingId) {
        await axios.put(`${API_BASE}/api/shipments/${editingId}`, body, { headers: authHeader });
      } else {
        await axios.post(`${API_BASE}/api/shipments`, body, { headers: authHeader });
      }
      setShowForm(false);
      await load();
    } catch (err) {
      setFormErrors([err.response?.data?.message || 'Failed to save.']);
    } finally {
      setSaving(false);
    }
  };

  const deleteShipment = async (s) => {
    if (!window.confirm('Delete this shipment? This cannot be undone.')) return;
    try {
      await axios.delete(`${API_BASE}/api/shipments/${s._id}`, { headers: authHeader });
      await load();
    } catch (err) {
      window.alert(err.response?.data?.message || 'Failed to delete.');
    }
  };

  // ── Inline status update — right from the row, no need to open Edit ───────
  const updateStatusInline = async (s, newStatus) => {
    if (newStatus === s.status) return;
    setStatusUpdatingId(s._id);
    try {
      await axios.put(`${API_BASE}/api/shipments/${s._id}`, { status: newStatus, orderId: s.orderId || null }, { headers: authHeader });
      await load();
    } catch (err) {
      window.alert(err.response?.data?.message || 'Failed to update status.');
    } finally {
      setStatusUpdatingId(null);
    }
  };

  const resetFilters = () => {
    setSearch(''); setFrom(''); setTo('');
    setFilterPartner(''); setFilterCountry(''); setFilterState(''); setFilterCity(''); setFilterStatus(''); setOnlyMissing(false);
  };
  const hasActiveFilters = search || from || to || filterPartner || filterCountry || filterState || filterCity || filterStatus || onlyMissing;
  // The status filter offers the statuses the current tab can hold.
  const statusOptions = STATUS_OPTIONS.filter(st => (tab === 'active' ? !COMPLETED_STATUSES.includes(st) : COMPLETED_STATUSES.includes(st)));

  const partnerOptions = useMemo(() => partners.map(p => p.name).sort(), [partners]);
  const countryOptions = useMemo(() => distinctSorted(shipments, 'country'), [shipments]);
  const stateOptions = useMemo(() => distinctSorted(shipments, 'state'), [shipments]);
  const cityOptions = useMemo(() => distinctSorted(shipments, 'city'), [shipments]);

  const activeCount = shipments.filter(s => !COMPLETED_STATUSES.includes(s.status)).length;
  const completedCount = shipments.filter(s => COMPLETED_STATUSES.includes(s.status) && !isArchived(s)).length;
  const archiveCount = shipments.filter(isArchived).length;
  const missingCount = shipments.filter(s => !isArchived(s) && (missingOf(s).awb || missingOf(s).photo)).length;

  const rows = shipments.filter(s => {
    if (tab === 'active' && COMPLETED_STATUSES.includes(s.status)) return false;
    if (tab === 'completed' && (!COMPLETED_STATUSES.includes(s.status) || isArchived(s))) return false;
    if (tab === 'archive' && !isArchived(s)) return false;
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      const hay = `${s.recipientName} ${s.trackingId} ${s.city} ${s.state}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    if (filterPartner && s.shippingPartner !== filterPartner) return false;
    if (filterCountry && s.country !== filterCountry) return false;
    if (filterState && s.state !== filterState) return false;
    if (filterCity && s.city !== filterCity) return false;
    if (filterStatus && s.status !== filterStatus) return false;
    if (onlyMissing && !missingOf(s).awb && !missingOf(s).photo) return false;
    const anchor = new Date(s.shippedDate || s.createdAt);
    if (from && anchor < new Date(from)) return false;
    if (to) { const end = new Date(to); end.setHours(23, 59, 59, 999); if (anchor > end) return false; }
    return true;
  });


  return (
    <div className="air air-forms" style={{ minHeight: '100vh', background: 'var(--navy)', color: '#222222', fontFamily: "'Jost', sans-serif" }}>
      <style>{MARQLAND_THEME_CSS}</style>
      <style>{AIR_CSS}</style>

      <NavBar authState="logout" onLogout={onLogout} mobileMenu />

      <div style={{ maxWidth: 1360, margin: '0 auto', padding: '108px 24px 60px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 16, marginBottom: 8 }}>
          <div>
            <span className="pill">Courier Portal</span>
            <h1 className="sf" style={{ fontSize: 28, fontWeight: 400, marginTop: 10 }}>Welcome, {session.user.name}</h1>
          </div>
          <button onClick={openCreate} className="btn-gold" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Plus size={14} /> New Shipment
          </button>

          {/* Search + filters — compact single bar, wraps under the heading on narrow screens */}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', width: '100%' }}>
            <div style={{ position: 'relative', flex: '1 1 180px', minWidth: 150 }}>
              <Search size={16} style={{ position: 'absolute', left: 14, top: 16, color: '#222222' }} />
              <input className="fi" style={{ paddingLeft: 40 }} placeholder="Search shipments" value={search} onChange={e => setSearch(e.target.value)} />
            </div>
            <AirSelect boxed value={filterPartner} onChange={setFilterPartner} options={[{ v: '', l: 'All partners' }, ...partnerOptions]} searchable={partnerOptions.length > 8} ariaLabel="All partners" style={{ flex: '0 1 170px', minWidth: 140 }} />
            <AirSelect boxed value={filterCountry} onChange={setFilterCountry} options={[{ v: '', l: 'All countries' }, ...countryOptions]} searchable={countryOptions.length > 8} ariaLabel="All countries" style={{ flex: '0 1 170px', minWidth: 140 }} />
            <AirSelect boxed value={filterState} onChange={setFilterState} options={[{ v: '', l: 'All states' }, ...stateOptions]} searchable={stateOptions.length > 8} ariaLabel="All states" style={{ flex: '0 1 170px', minWidth: 140 }} />
            <AirSelect boxed value={filterCity} onChange={setFilterCity} options={[{ v: '', l: 'All cities' }, ...cityOptions]} searchable={cityOptions.length > 8} ariaLabel="All cities" style={{ flex: '0 1 170px', minWidth: 140 }} />
            <AirSelect boxed value={filterStatus} onChange={setFilterStatus} options={[{ v: '', l: 'All statuses' }, ...statusOptions]} ariaLabel="All statuses" style={{ flex: '0 1 170px', minWidth: 140 }} />
            {missingCount > 0 && (
              <button type="button" onClick={() => setOnlyMissing(v => !v)} aria-pressed={onlyMissing} style={{
                ...MISSING_BADGE, fontSize: 13, padding: '8px 12px', minHeight: 40, cursor: 'pointer',
                ...(onlyMissing ? { background: '#8a5a00', color: '#fff', borderColor: '#8a5a00' } : {}),
              }}>
                <AlertTriangle size={13} /> Missing AWB / photo ({missingCount})
              </button>
            )}
            <AirDatePicker range bar nights={false} min="" labels={['Start date', 'End date']}
              value={[from, to]} onChange={([a, b]) => { setFrom(a); setTo(b); }} />
            {hasActiveFilters && (
              <button onClick={resetFilters} title="Reset filters" style={{
                display: 'flex', alignItems: 'center', gap: 5, background: 'none',
                border: '1px solid #dddddd', color: '#484848',
                padding: '6px 10px', fontWeight: 600, fontSize: 12,
                cursor: 'pointer', borderRadius: 2, flexShrink: 0
              }}>
                <RotateCcw size={11} /> Reset
              </button>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="air-tabs" role="tablist" style={{ marginTop: 20, marginBottom: 24, gap: isMobile ? 20 : 28 }}>
          {TABS.map(t => (
            <button
              key={t.id}
              role="tab"
              aria-selected={tab === t.id}
              className="air-tab"
              onClick={() => {
                setTab(t.id);
                // A status from the other tab would just empty the list.
                if (filterStatus && (t.id === 'active') === COMPLETED_STATUSES.includes(filterStatus)) setFilterStatus('');
              }}
              style={{ display: 'flex', alignItems: 'center', gap: 8, whiteSpace: 'nowrap' }}
            >
              <t.icon size={16} /> {t.label} ({t.id === 'active' ? activeCount : t.id === 'completed' ? completedCount : archiveCount})
            </button>
          ))}
        </div>

        {loadError && <p style={{ color: '#c13515', fontSize: 12, marginBottom: 16 }}>{loadError}</p>}

        {/* Row list — compact, single-line-per-shipment */}
        {loading ? (
          <p style={{ textAlign: 'center', color: '#6a6a6a', padding: 40 }}>
            <Loader2 size={16} className="animate-spin" style={{ marginRight: 8, verticalAlign: 'middle' }} /> Loading…
          </p>
        ) : rows.length === 0 ? (
          <p style={{ textAlign: 'center', color: '#6a6a6a', padding: 40 }}>No shipments in {tab}.</p>
        ) : isCompact ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {rows.map(s => (
              <div key={s._id} onClick={e => { if (!isControlClick(e)) setViewing(s); }} style={{ border: '1px solid #dddddd', borderRadius: 12, padding: '14px 14px 12px', display: 'flex', flexDirection: 'column', gap: 8, cursor: 'pointer' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'flex-start' }}>
                  <div style={{ minWidth: 0 }}>
                    <p style={{ fontSize: 14, color: '#222222', margin: 0 }}>
                      <Truck size={12} style={{ verticalAlign: 'middle', marginRight: 6 }} />{s.recipientName}
                    </p>
                    <p style={{ fontSize: 12, color: '#6a6a6a', margin: '2px 0 0' }}>
                      {[s.recipientAddress, s.city, s.state, s.country].filter(Boolean).join(', ') || '—'}
                    </p>
                  </div>
                  {/* Edit/Delete — active tab only; never available on completed/archive */}
                  <div style={{ display: 'flex', gap: 10 }}>
                    {tab === 'active' && (
                      <>
                        <button onClick={() => openEdit(s)} title="Edit" style={{ background: 'none', border: 'none', color: '#484848', cursor: 'pointer', display: 'flex' }}>
                          <Pencil size={14} />
                        </button>
                        <button onClick={() => deleteShipment(s)} title="Delete" style={{ background: 'none', border: 'none', color: '#c13515', cursor: 'pointer', display: 'flex' }}>
                          <Trash2 size={14} />
                        </button>
                      </>
                    )}
                  </div>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 14px', fontSize: 12, color: '#484848' }}>
                  {s.trackingId && <span style={{ color: '#7d6233' }}>{s.trackingId}</span>}
                  <span>{s.shippingPartner || 'No partner'}</span>
                  {s.phone && <span>{s.phone}</span>}
                  <span>Shipped {fmtDate(s.shippedDate)}</span>
                  {boxesLabel(s.boxes) && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><Boxes size={12} /> {boxesLabel(s.boxes)}</span>}
                  {s.photoUrl && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><ImageIcon size={12} /> {photosOf(s).length > 1 ? `${photosOf(s).length} photos` : 'Photo'}</span>}
                </div>
                {(missingOf(s).awb || missingOf(s).photo) && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
                    <MissingFlags s={s} />
                    {rowFixes(s)}
                  </div>
                )}
                  {/* Inline status update — active/completed only; archive is read-only */}
                  <div>
                    {tab !== 'archive' ? (
                      <AirSelect boxed value={s.status} disabled={statusUpdatingId === s._id} onChange={v => updateStatusInline(s, v)} options={STATUS_OPTIONS} align="right" ariaLabel="Update status" style={{ minWidth: 150 }} />
                    ) : (
                      <span style={{
                        fontWeight: 600, fontSize: 12, padding: '3px 8px',
                        color: '#008a05', border: '1px solid rgba(126,201,138,0.4)', display: 'inline-block'
                      }}>
                        {s.status}
                      </span>
                    )}
                  </div>

              </div>
            ))}
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <div style={{ minWidth: 1160, display: 'flex', flexDirection: 'column' }}>
              {/* Header row */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: '150px 1fr 170px 130px 140px 150px 110px',
                gap: 12, padding: '0 14px 8px', borderBottom: '1px solid #dddddd',
                fontWeight: 600, fontSize: 12, color: '#6a6a6a'
              }}>
                <span>Tracking / Partner</span>
                <span>Recipient / Address</span>
                <span>Location</span>
                <span>Phone</span>
                <span>Status</span>
                <span>Shipped</span>
                <span>Actions</span>
              </div>

              {rows.map(s => (
                <div key={s._id} onClick={e => { if (!isControlClick(e)) setViewing(s); }} style={{
                  display: 'grid',
                  gridTemplateColumns: '150px 1fr 170px 130px 140px 150px 110px',
                  gap: 12, alignItems: 'center', cursor: 'pointer',
                  padding: '10px 14px', borderBottom: '1px solid #dddddd',
                }}>
                  <div style={{ minWidth: 0 }}>
                    <p style={{ fontSize: 12, color: '#7d6233', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={s.trackingId}>
                      {s.trackingId || '—'}
                    </p>
                    <p style={{ fontSize: 12, color: '#6a6a6a', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={s.shippingPartner}>
                      {s.shippingPartner || 'No partner'}
                    </p>
                    {(boxesLabel(s.boxes) || s.photoUrl) && (
                      <p style={{ fontSize: 12, color: '#484848', margin: '2px 0 0', display: 'flex', gap: 8, alignItems: 'center' }}>
                        {boxesLabel(s.boxes) && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}><Boxes size={12} /> {boxesLabel(s.boxes)}</span>}
                        {s.photoUrl && <span title="Click the row to view the photo" style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}><ImageIcon size={12} /> Photo</span>}
                      </p>
                    )}
                    {(missingOf(s).awb || missingOf(s).photo) && <div style={{ marginTop: 4 }}><MissingFlags s={s} /></div>}
                  </div>

                  <div style={{ minWidth: 0 }}>
                    <p style={{ fontSize: 13, color: '#222222', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={s.recipientName}>
                      <Truck size={12} style={{ verticalAlign: 'middle', marginRight: 6 }} />{s.recipientName}
                    </p>
                    <p style={{ fontSize: 12, color: '#6a6a6a', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={s.recipientAddress}>
                      {s.recipientAddress || '—'}
                    </p>
                  </div>

                  <div style={{ minWidth: 0, fontSize: 12, color: '#484848', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    title={[s.city, s.state, s.country].filter(Boolean).join(', ')}>
                    {[s.city, s.state, s.country].filter(Boolean).join(', ') || '—'}
                  </div>

                  <div style={{ fontSize: 12, color: '#484848', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {s.phone || '—'}
                  </div>

                  {/* Inline status update — active/completed only; archive is read-only */}
                  <div>
                    {tab !== 'archive' ? (
                      <AirSelect boxed value={s.status} disabled={statusUpdatingId === s._id} onChange={v => updateStatusInline(s, v)} options={STATUS_OPTIONS} align="right" ariaLabel="Update status" style={{ minWidth: 150 }} />
                    ) : (
                      <span style={{
                        fontWeight: 600, fontSize: 12, padding: '3px 8px',
                        color: '#008a05', border: '1px solid rgba(126,201,138,0.4)', display: 'inline-block'
                      }}>
                        {s.status}
                      </span>
                    )}
                  </div>

                  <div style={{ fontSize: 12, color: '#6a6a6a' }}>{fmtDate(s.shippedDate)}</div>

                  {/* Edit/Delete — active tab only; never available on completed/archive */}
                  <div style={{ display: 'flex', gap: 10 }}>
                    {tab === 'active' && (
                      <>
                        <button onClick={() => openEdit(s)} title="Edit" style={{ background: 'none', border: 'none', color: '#484848', cursor: 'pointer', display: 'flex' }}>
                          <Pencil size={14} />
                        </button>
                        <button onClick={() => deleteShipment(s)} title="Delete" style={{ background: 'none', border: 'none', color: '#c13515', cursor: 'pointer', display: 'flex' }}>
                          <Trash2 size={14} />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ── Create/Edit form modal ── */}
      {showForm && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 300, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: isMobile ? 12 : 24 }}>
          <div style={{ background: '#ffffff', border: 'none', borderRadius: 12, boxShadow: '0 6px 20px rgba(0,0,0,.2)', width: '100%', maxWidth: 600, maxHeight: '88vh', overflowY: 'auto', padding: isMobile ? 20 : 32, position: 'relative' }}>
            <button onClick={() => setShowForm(false)} style={{ position: 'absolute', top: 16, right: 16, background: 'none', border: 'none', cursor: 'pointer', color: '#6a6a6a' }}>
              <X size={18} />
            </button>
            <h2 className="sf" style={{ fontSize: 22, color: '#7d6233', fontWeight: 500, marginBottom: 20 }}>
              {editingId ? 'Edit Shipment' : 'New Shipment'}
            </h2>

            <input ref={labelInput} type="file" accept="image/*" capture="environment" hidden
              onChange={e => { scanLabel(e.target.files?.[0]); e.target.value = ''; }} />
            <div style={{ marginBottom: 18 }}>
              <button type="button" onClick={() => labelInput.current?.click()} disabled={scanningLabel} style={{
                ...ROW_FIX_BTN, width: '100%', justifyContent: 'center', fontSize: 14, padding: '12px 14px', minHeight: 46,
              }}>
                {scanningLabel ? <Loader2 size={15} className="animate-spin" /> : <CameraIcon size={15} />}
                {scanningLabel ? 'Reading label…' : 'Scan label photo'}
              </button>
              <p style={{ fontSize: 12, color: labelNote.startsWith('Label read') || !labelNote ? '#6a6a6a' : '#c13515', margin: '6px 0 0' }}>
                {labelNote || 'A close-up of the To label fills the recipient details and is saved as the shipment photo.'}
              </p>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 16, marginBottom: 8 }}>
              <div>
                <label className="lbl">Shipped Date *</label>
                <AirDatePicker compact label="Shipped on" min="" value={form.shippedDate} onChange={d => setForm(f => ({ ...f, shippedDate: d }))} />
              </div>
              <div>
                <label className="lbl">Status</label>
                <AirSelect boxed value={form.status} onChange={v => setForm(f => ({ ...f, status: v }))} options={STATUS_OPTIONS} ariaLabel="Status" />
              </div>
              <div>
                <label className="lbl">Recipient Name *</label>
                <input className="fi" value={form.recipientName} onChange={e => setForm(f => ({ ...f, recipientName: e.target.value }))} />
              </div>
              <div>
                <label className="lbl">Phone</label>
                <input className="fi" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} />
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label className="lbl">Recipient Address</label>
                <input className="fi" style={{ width: '100%' }} value={form.recipientAddress} onChange={e => setForm(f => ({ ...f, recipientAddress: e.target.value }))} />
              </div>

              {/* State first, then City — city stays disabled until a state is picked */}
              <div>
                <label className="lbl" style={{ display: 'block', marginBottom: 4 }}>State</label>
                <AirSelect boxed searchable
                  value={form.state}
                  onChange={handleStateChange}
                  options={INDIA_STATES}
                  placeholder="Select state"
                  ariaLabel="State"
                />
              </div>
              <div>
                <label className="lbl" style={{ display: 'block', marginBottom: 4 }}>
                  City {form.state && (
                    <button type="button" onClick={() => setCityManual(m => !m)}
                      style={{ background: 'none', border: 'none', color: '#7d6233', cursor: 'pointer', fontSize: 12, textTransform: 'none', letterSpacing: 0, marginLeft: 8, textDecoration: 'underline' }}>
                      {cityManual ? 'choose from list' : "can't find it? enter manually"}
                    </button>
                  )}
                </label>
                {cityManual ? (
                  <input className="fi" style={{ width: '100%' }} value={form.city} placeholder="Enter city"
                    onChange={e => setForm(f => ({ ...f, city: e.target.value }))} />
                ) : (
                  <AirSelect boxed searchable
                    value={form.city}
                    onChange={(c) => setForm(f => ({ ...f, city: c }))}
                    options={form.state ? (CITIES_BY_STATE[form.state] || []) : []}
                    placeholder={form.state ? 'Select city' : 'Select a state first'}
                    disabled={!form.state}
                    ariaLabel="City"
                  />
                )}
              </div>

              <div>
                <label className="lbl">Country</label>
                <input className="fi" value={form.country} onChange={e => setForm(f => ({ ...f, country: e.target.value }))} />
              </div>
              <div>
                <label className="lbl">Tracking ID</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input className="fi" style={{ flex: 1, minWidth: 0 }} value={form.trackingId}
                    onChange={e => setForm(f => ({ ...f, trackingId: e.target.value }))} />
                  <button type="button" onClick={() => pickAwbPhoto('form')} disabled={awbBusyId === 'form'} title="Read the AWB from a photo of its barcode" style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center', width: 40, flexShrink: 0,
                    background: 'none', border: '1px solid #dddddd', color: '#7d6233',
                    cursor: 'pointer', borderRadius: 2,
                  }}>
                    {awbBusyId === 'form' ? <Loader2 size={15} className="animate-spin" /> : <CameraIcon size={15} />}
                  </button>
                  {isMobile && LIVE_SCAN && (
                    <button type="button" onClick={() => setShowScanner(true)} title="Scan barcode with the camera" style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'center', width: 40, flexShrink: 0,
                      background: 'none', border: '1px solid #dddddd', color: '#7d6233',
                      cursor: 'pointer', borderRadius: 2,
                    }}>
                      <ScanBarcode size={15} />
                    </button>
                  )}
                </div>
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label className="lbl">Shipping Partner</label>
                <input className="fi" style={{ width: '100%' }} list="courier-shipping-partners" value={form.shippingPartner}
                  onChange={e => setForm(f => ({ ...f, shippingPartner: e.target.value }))} />
                <datalist id="courier-shipping-partners">
                  {partners.map(p => <option key={p._id} value={p.name} />)}
                </datalist>
              </div>
              <div>
                <label className="lbl">Number of Boxes</label>
                <input className="fi" type="number" inputMode="numeric" min={0} placeholder="e.g. 2" value={form.boxes}
                  onChange={e => setForm(f => ({ ...f, boxes: cleanBoxes(e.target.value) }))} />
              </div>
              <div>
                <label className="lbl">Shipment Photos</label>
                <input ref={photoInput} type="file" accept="image/*" multiple hidden onChange={e => { uploadPhotos(e.target.files); e.target.value = ''; }} />
                <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
                  {photosOf(form).map((url, i) => safeImg(url) && (
                    <div key={url} style={{ position: 'relative' }}>
                      <a href={safeImg(url)} target="_blank" rel="noopener noreferrer" title={i ? 'Open photo' : 'Open main photo'}>
                        <img src={safeImg(url)} alt={i ? `Shipment, view ${i + 1}` : 'Shipment'} style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 8, border: `1px solid ${i ? '#dddddd' : '#222222'}`, display: 'block' }} />
                      </a>
                      {!photoBusy && (
                        <button type="button" aria-label="Remove photo" title="Remove photo"
                          onClick={() => setForm(f => ({ ...f, ...photoFields(photosOf(f).filter((_, j) => j !== i)) }))}
                          style={{ position: 'absolute', top: -6, right: -6, width: 20, height: 20, borderRadius: 10, border: 'none', background: '#222222', color: '#fff', display: 'grid', placeItems: 'center', padding: 0, cursor: 'pointer' }}>
                          <X size={12} />
                        </button>
                      )}
                    </div>
                  ))}
                  <button type="button" onClick={() => photoInput.current?.click()} disabled={photoBusy || photosOf(form).length >= MAX_PHOTOS} style={{
                    display: 'flex', alignItems: 'center', gap: 6, background: '#fff', border: '1px solid #b0b0b0', borderRadius: 8,
                    padding: '10px 14px', cursor: photoBusy ? 'default' : 'pointer', fontSize: 14, color: '#222222',
                  }}>
                    {photoBusy ? <Loader2 size={14} className="animate-spin" /> : <ImagePlus size={14} />}
                    {photoBusy ? 'Uploading…' : form.photoUrl ? 'Add more' : 'Add photos'}
                  </button>
                </div>
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label className="lbl">Notes</label>
                <textarea className="fi" rows={2} style={{ width: '100%', resize: 'none' }} value={form.notes}
                  onChange={e => setForm(f => ({ ...f, notes: sanitizeMessage(e.target.value) }))} />
              </div>
            </div>

            {formErrors.length > 0 && (
              <div style={{ margin: '16px 0' }}>
                {formErrors.map((e, i) => <p key={i} style={{ fontSize: 12, color: '#c13515' }}>{e}</p>)}
              </div>
            )}

            <button onClick={submitForm} disabled={saving || photoBusy} className="btn-gold" style={{ width: '100%', justifyContent: 'center', marginTop: 16 }}>
              {saving ? 'Saving…' : editingId ? 'Save Changes' : 'Create Shipment'}
            </button>
          </div>
        </div>
      )}

      {viewing && (
        <ShipmentDetails s={viewing} onClose={() => setViewing(null)} onEdit={tab === 'active' ? openEdit : null} />
      )}

      {/* ── Barcode scan overlay — opened from the Tracking ID field, mobile only ── */}
      <input ref={awbInput} type="file" accept="image/*" capture="environment" hidden
        onChange={e => { readAwbPhoto(e.target.files?.[0]); e.target.value = ''; }} />
      <input ref={rowPhotoInput} type="file" accept="image/*" multiple hidden
        onChange={e => { uploadRowPhotos(e.target.files); e.target.value = ''; }} />

      {showScanner && (
        <BarcodeScannerModal
          onDetected={handleBarcodeDetected}
          onClose={() => setShowScanner(false)}
        />
      )}
    </div>
  );
};

export default CourierPortal;